import { useEffect, useRef, useState } from 'react';
import { useReveal } from '../lib/anim/useReveal';

const WRAP_CSS = `
@keyframes wrap-shimmer {
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(400%); }
}
.wrap-field-shimmer {
  position: relative;
  overflow: hidden;
}
.wrap-field-shimmer::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent 0%, var(--color-accent-soft) 50%, transparent 100%);
  transform: translateX(-100%);
  animation: wrap-shimmer .8s ease forwards;
}
.wrap-status-pill {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 500;
  transition: background .4s ease, color .4s ease, border-color .4s ease;
}
.wrap-status-pill.draft {
  background: rgba(11, 18, 32, 0.05);
  color: var(--color-mute);
  border: 1px solid var(--color-hairline);
}
.wrap-status-pill.ready {
  background: var(--color-accent-soft);
  color: var(--color-accent);
  border: 1px solid rgba(79, 70, 229, 0.3);
}
.wrap-status-pill.live {
  background: var(--color-accent);
  color: #FFFFFF;
  border: none;
}
.wrap-save-flash {
  transition: background .2s ease, color .2s ease;
}
.wrap-save-flash.flash {
  background: var(--color-accent) !important;
  color: #fff !important;
  border-color: var(--color-accent) !important;
}
@media (prefers-reduced-motion: reduce) {
  .wrap-field-shimmer::after { animation: none; }
  .wrap-status-pill, .wrap-save-flash { transition: none; }
}
`;

let wrapCSSInjected = false;
function injectWrapCSS() {
  if (wrapCSSInjected || typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.textContent = WRAP_CSS;
  document.head.appendChild(style);
  wrapCSSInjected = true;
}

type WrapStatusPhase = 'blank' | 'draft' | 'ready' | 'live';

const wait = (ms: number) => new Promise<void>((res) => setTimeout(res, ms));

export function MockupWrap() {
  const sectionHeading = useReveal();
  const bodyReveal = useReveal({ delay: 1 });
  const mockupReveal = useReveal({ delay: 2 });

  const stageRef = useRef<HTMLDivElement>(null);
  const [productName, setProductName] = useState('');
  const [inputFormat, setInputFormat] = useState('');
  const [outputFormat, setOutputFormat] = useState('');
  const [pricing, setPricing] = useState('');
  const [statusPhase, setStatusPhase] = useState<WrapStatusPhase>('blank');
  const [shimmerFields, setShimmerFields] = useState<Set<string>>(new Set());
  const [saveFlash, setSaveFlash] = useState(false);
  const hasPlayed = useRef(false);

  useEffect(() => {
    injectWrapCSS();
  }, []);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;

    const prefersReduced =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced) {
      setProductName('Power BI Auditor');
      setInputFormat('pbix');
      setOutputFormat('pdf');
      setPricing('$9 per run');
      setStatusPhase('live');
      return;
    }

    if (!('IntersectionObserver' in window)) {
      playSequence();
      return;
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasPlayed.current) {
          hasPlayed.current = true;
          io.unobserve(entry.target);
          playSequence();
        }
      },
      { threshold: 0.3 }
    );
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function playSequence() {
    await wait(500);

    setShimmerFields(new Set(['name']));
    setProductName('Power BI Auditor');
    await wait(280);

    setShimmerFields(new Set(['input']));
    setInputFormat('pbix');
    await wait(280);

    setShimmerFields(new Set(['output']));
    setOutputFormat('pdf');
    await wait(280);

    setShimmerFields(new Set(['pricing']));
    setPricing('$9 per run');
    setStatusPhase('draft');
    await wait(400);

    setStatusPhase('ready');
    await wait(600);

    // save flash
    setSaveFlash(true);
    await wait(300);
    setSaveFlash(false);
    await wait(200);
    setStatusPhase('live');
  }

  const cardStyle: React.CSSProperties = {
    background: 'var(--color-surface)',
    border: '1px solid var(--color-hairline)',
    borderRadius: '20px',
    padding: '24px',
  };

  const fieldStyle: React.CSSProperties = {
    background: 'var(--color-bg)',
    border: '1px solid var(--color-hairline)',
    borderRadius: '8px',
    padding: '8px 12px',
    fontSize: '13px',
    color: 'var(--color-ink)',
    fontFamily: 'var(--font-body)',
    minHeight: '36px',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '11px',
    fontWeight: 500,
    color: 'var(--color-mute)',
    fontFamily: 'var(--font-body)',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    marginBottom: '4px',
    display: 'block',
  };

  const statusLabel: Record<WrapStatusPhase, string> = {
    blank: 'draft',
    draft: 'draft',
    ready: 'ready',
    live: 'live',
  };

  const statusClass: Record<WrapStatusPhase, string> = {
    blank: 'draft',
    draft: 'draft',
    ready: 'ready',
    live: 'live',
  };

  return (
    <section
      className="py-24"
      style={{ background: 'var(--color-bg)', borderTop: '1px solid var(--color-hairline)' }}
    >
      <div className="max-w-6xl mx-auto px-6">
        {/* Section heading */}
        <div
          ref={sectionHeading.ref}
          {...sectionHeading.props}
          className={`mb-4${sectionHeading.props.className ? ' ' + sectionHeading.props.className : ''}`}
        >
          <p className="eyebrow" data-r>
            <span className="eyebrow-dash" />
            <span>03 · WRAP</span>
          </p>
          <h2
            className="text-2xl md:text-3xl font-bold max-w-xl"
            style={{ fontFamily: 'var(--font-display)', color: 'var(--color-ink)' }}
          >
            Configure your copy, your ICP, your price.
          </h2>
        </div>

        <div
          ref={bodyReveal.ref}
          {...bodyReveal.props}
          className={`mb-10${bodyReveal.props.className ? ' ' + bodyReveal.props.className : ''}`}
        >
          <p
            className="text-base max-w-lg"
            style={{ color: 'var(--color-mute)', fontFamily: 'var(--font-body)' }}
          >
            The only part that&rsquo;s yours. Everything else is already built.
          </p>
        </div>

        {/* Mockup */}
        <div
          ref={(el) => {
            (mockupReveal.ref as React.MutableRefObject<HTMLDivElement | null>).current = el;
            (stageRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
          }}
          {...mockupReveal.props}
          className={`max-w-2xl${mockupReveal.props.className ? ' ' + mockupReveal.props.className : ''}`}
        >
          <div style={cardStyle}>
            <div
              className="flex items-center justify-between mb-5"
              style={{ paddingBottom: '12px', borderBottom: '1px solid var(--color-hairline)' }}
            >
              <p
                style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: 'var(--color-mute)',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  fontFamily: 'var(--font-mono)',
                }}
              >
                Wrap configuration
              </p>
              <span className={`wrap-status-pill ${statusClass[statusPhase]}`} style={{ fontFamily: 'var(--font-mono)' }}>
                {statusLabel[statusPhase]}
              </span>
            </div>

            <div className="space-y-4">
              <div>
                <label style={labelStyle}>Product name</label>
                <div
                  className={shimmerFields.has('name') ? 'wrap-field-shimmer' : ''}
                  style={fieldStyle}
                >
                  {productName || <span style={{ color: 'var(--color-hairline)' }}>—</span>}
                </div>
              </div>
              <div>
                <label style={labelStyle}>Input format</label>
                <div
                  className={shimmerFields.has('input') ? 'wrap-field-shimmer' : ''}
                  style={fieldStyle}
                >
                  {inputFormat || <span style={{ color: 'var(--color-hairline)' }}>—</span>}
                </div>
              </div>
              <div>
                <label style={labelStyle}>Output format</label>
                <div
                  className={shimmerFields.has('output') ? 'wrap-field-shimmer' : ''}
                  style={fieldStyle}
                >
                  {outputFormat || <span style={{ color: 'var(--color-hairline)' }}>—</span>}
                </div>
              </div>
              <div>
                <label style={labelStyle}>Pricing</label>
                <div
                  className={shimmerFields.has('pricing') ? 'wrap-field-shimmer' : ''}
                  style={fieldStyle}
                >
                  {pricing || <span style={{ color: 'var(--color-hairline)' }}>—</span>}
                </div>
              </div>
            </div>

            <button
              type="button"
              className={`mt-5 px-5 py-2.5 text-sm font-medium transition-all wrap-save-flash${saveFlash ? ' flash' : ''}`}
              style={{
                background: 'var(--color-accent)',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '6px',
                fontFamily: 'var(--font-body)',
                cursor: 'default',
              }}
            >
              Save wrap
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
